import { ArrowTopRightOnSquareIcon, ExclamationTriangleIcon } from "@heroicons/react/24/solid";
import { Avatar } from "@moe/oss/ui/avatar";
import { CardPattern } from "@moe/oss/ui/card-pattern";
import { ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuTrigger } from "@moe/oss/ui/context-menu";
import { cn } from "@moe/oss/utils/utils";
import { Tip } from "@moe/priv/components/tip";
import { useKeyHeld } from "@moe/priv/hooks/use-key-held";
import { Character } from "@moe/priv/model/character";
import { Link } from "@tanstack/react-router";
import { useCopyToClipboard } from "@uidotdev/usehooks";
import { CharacterCard } from "@web/components/CharacterCard";
import { ChatButton } from "@web/components/ChatButton";
import { LikeButton } from "@web/components/LikeButton";
import { ReportModal } from "@web/components/ReportModal";
import { TagChip } from "@web/components/tag/TagChip";
import { useCreateChat } from "@web/hooks/use-create-chat";
import { useLikeChar } from "@web/hooks/use-like-char";
import { useTagNavigation } from "@web/route-services/index/use-tag-navigation";
import { EyeIcon, LinkIcon } from "lucide-react";
import { motion, useMotionValue } from "motion/react";
import React from "react";
import { toast } from "sonner";
import { useAppContext } from "../root/AppContext";

interface Props {
  char: Character;
  isPending: boolean;
  [key: string]: any;
}

/**
 * An expansive, wide, horizontally laid out character card.
 * We only use this on desktop, it's too large for mobile.
 */
export function HorizontalCard({ char, isPending }: Props) {
  if (isPending) return <Loading />;
  if (char && !isPending) return <Loaded char={char} />;
}

function Loading() {
  return (
    <motion.div
      className="bg-moe-card border-line relative flex h-[275px] w-[500px] cursor-pointer overflow-hidden rounded-3xl border shadow-md"
      initial={{ opacity: 0.7 }}
      animate={{ opacity: [0.7, 1, 0.7] }}
      transition={{ repeat: Infinity, duration: 1.5, ease: "easeInOut" }}
    >
      <div className="bg-muted h-full w-[200px]" />

      <div className="flex h-full w-[300px] flex-col pl-4 pr-3">
        <div className="bg-muted mt-5 h-[24px] w-3/4 rounded" />
        <div className="bg-muted mt-2 h-[60px] w-full rounded" />

        <div className="mt-2 flex flex-wrap gap-1">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="bg-muted h-[24px] w-[60px] rounded" />
          ))}
        </div>

        <div className="mb-2 mt-auto flex items-center justify-between">
          <div className="bg-muted h-[20px] w-[100px] rounded" />
        </div>
      </div>
    </motion.div>
  );
}

interface LoadedProps {
  char: Character;
}

function Loaded({ char }: LoadedProps) {
  const { createModal, doFocusExploreRef, isDesktopScreen } = useAppContext();
  const { likeCount, isLiked, like } = useLikeChar({ char });
  const createChat = useCreateChat();
  const [_, copyToClipboard] = useCopyToClipboard();

  const tagsLimit = isDesktopScreen ? 10 : 0;
  const tags = char.tags?.splice(0, tagsLimit);

  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  function onMouseMove({ currentTarget, clientX, clientY }: any) {
    const { left, top } = currentTarget.getBoundingClientRect();
    mouseX.set(clientX - left);
    mouseY.set(clientY - top);
  }

  const { add, only } = useTagNavigation();
  const isCtrlHeld = useKeyHeld("Control");

  const handleTagSelect = (tag: string) => {
    doFocusExploreRef.current = true;
    if (isCtrlHeld) {
      add(tag);
      return;
    }
    only(tag);
  };

  const cardClickHandler = () => {
    createModal({
      content: <CharacterCard char={char} />
    });
  };

  const handleOpenInNewTab = (e: React.MouseEvent) => {
    window.open(`/character/${char.id}`, "_blank", "noopener");
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button === 1) {
      e.preventDefault();
      handleOpenInNewTab(e);
    }
  };
  const handleShare = async () => {
    await copyToClipboard(`${window.location.origin}/character/${char.id}`);
    toast("Share link copied!");
  };

  if (isDesktopScreen) {
    return (
      <ContextMenu>
        <ContextMenuTrigger>
          <div className="h-full">
            <div className="h-full focus:outline-none">
              <div
                className={cn(
                  "group/card justify-top bg-moe-card border-line relative flex h-[275px] w-[500px]",
                  "cursor-pointer overflow-hidden rounded-3xl border"
                )}
                onMouseMove={onMouseMove}
                onClick={cardClickHandler}
                onMouseDown={handleMouseDown}
              >
                <CardPattern mouseX={mouseX} mouseY={mouseY} />
                <Avatar className="z-10 h-full w-[200px] rounded-none" src={char.avatar} />

                <div className="z-10 flex h-full w-[300px] flex-col pl-4 pr-3">
                  <div className="text-tx-primary line-clamp-1 w-full shrink-0 text-ellipsis pt-5 text-lg font-bold">
                    {char.title}
                  </div>
                  <div className="text-tx-secondary mb-2 line-clamp-3 shrink-0 text-ellipsis whitespace-pre-wrap text-base font-medium">
                    {char.tagline}
                  </div>
                  <div className="scroll-hidden flex-2 mb-2 flex grow flex-wrap content-start items-start gap-1 overflow-y-auto">
                    {tags?.map((tag, index) => <TagChip key={index} tag={tag} onSelect={handleTagSelect} />)}
                  </div>

                  <div className="mb-2 mt-auto flex h-9 shrink-0 select-none items-center justify-between">
                    {char.externalCreatedBy ? (
                      <Tip text="External Creator" className="w-fit" stopPropagation={true}>
                        <div className="text-md text-tx-tertiary w-fit truncate font-semibold transition duration-300 ease-out hover:underline">
                          {char.externalCreatedBy}
                        </div>
                      </Tip>
                    ) : (
                      <Link
                        to="/profile"
                        search={{ username: char.createdBy }}
                        onPointerDownCapture={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                        className="text-md text-tx-tertiary w-fit truncate font-semibold transition duration-300 ease-out hover:underline"
                      >
                        {`@${char.createdBy}`}
                      </Link>
                    )}

                    <div className="ml-4 mr-4 flex flex-row items-center space-x-3">
                      <ChatButton
                        size="md"
                        layout="horizontal"
                        color="secondary"
                        messageCount={char.messageCount}
                        onClick={(e) => {
                          e.stopPropagation();
                          createChat(char);
                        }}
                      />
                      <LikeButton
                        color="secondary"
                        layout="horizontal"
                        size="md"
                        likeCount={likeCount}
                        isLiked={isLiked}
                        onClick={(e) => {
                          e.stopPropagation();
                          like();
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </ContextMenuTrigger>
        <ContextMenuContent>
          <ContextMenuItem onClick={handleOpenInNewTab}>
            <ArrowTopRightOnSquareIcon className="mr-2 h-4 w-4" />
            Open in new tab
          </ContextMenuItem>
          <ContextMenuItem onClick={cardClickHandler}>
            <EyeIcon className="mr-2 h-4 w-4" />
            View details
          </ContextMenuItem>
          <ContextMenuItem onClick={() => (window.location.href = `/character/${char.id}`)}>
            <LinkIcon className="mr-2 h-4 w-4" />
            Go to page
          </ContextMenuItem>
          <ContextMenuItem onClick={() => createModal({ content: <ReportModal characterID={char.id} /> })}>
            <ExclamationTriangleIcon className="mr-2 h-4 w-4" />
            Report
          </ContextMenuItem>
        </ContextMenuContent>
      </ContextMenu>
    );
  }
}
